import './SideBar.scss';
import { useLayoutEffect, useRef } from 'react';

import chevronRight from '../../assets/images/icons/chevron-right.svg';
import moderatorIcon from '../../assets/images/icons/sidebar-icons/user.svg';
import usersIcon from '../../assets/images/icons/sidebar-icons/user-id.svg';
import venuesIcon from '../../assets/images/icons/sidebar-icons/venue.svg';
import eventsIcon from '../../assets/images/icons/sidebar-icons/events.svg';
import queriesIcon from '../../assets/images/icons/sidebar-icons/queries.svg';
import advertIcon from '../../assets/images/icons/sidebar-icons/adverts.svg';
import promoIcon from '../../assets/images/icons/sidebar-icons/promotions.svg';
import chatIcon from '../../assets/images/icons/sidebar-icons/chat.svg';
import withdrawalRequestIcon from '../../assets/images/icons/sidebar-icons/withdrawal_request.svg';
import settingsIcon from '../../assets/images/icons/sidebar-icons/settings.svg';
import { NavLink } from 'react-router-dom';
import { useBadgeCounts } from '../../contexts/BadgeCountsContext';

import BottomTabs from '../BottomTabs/BottomTabs'

function SideBar() {
    const { counts } = useBadgeCounts();
    const sidebarRef = useRef(null);

    // The sidebar is position:fixed with its own scroll and it remounts on
    // every route change (each page renders its own <SideBar/>). Without this,
    // the scroll snaps back to the top on navigation, hiding lower nav items
    // and the active one. Persist + restore the scroll position across mounts.
    useLayoutEffect(() => {
        const el = sidebarRef.current;
        if (!el) return;
        const saved = sessionStorage.getItem('sidebarScroll');
        if (saved) el.scrollTop = Number(saved);
        const onScroll = () => sessionStorage.setItem('sidebarScroll', String(el.scrollTop));
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const badgeMap = {
        'withdrawal-requests': counts.withdrawals,
        'refund-requests': counts.disputes,
        'moderators': counts.permissions,
        'queries': counts.queries,
        'verifications': counts.verifications,
        'elite': counts.elite,
        'join-requests': counts.joinRequests,
        'brands': counts.brands,
        'venue-broadcasts': counts.venueBroadcasts,
    };

    const sideBarContent = [
        // AI Analyst sits first: it is the landing route and the fastest way
        // to answer 'what happened' without knowing which tab holds it.
        { id: 46, avatar: 'solar:chat-square-code-outline', text: 'AI Analyst', url: 'ai-analyst', iconifyIconString: true },
        // Permissions pinned to the top as the most-used surface.
        { id: 1, avatar: moderatorIcon, text: 'Permissions', url: 'moderators' },
        // My Organizer Roles hidden from the sidebar — superseded by
        // the new Organizers tab which lists everyone's roles. Route
        // /my-organizer still works if linked directly.
        // { id: 31, avatar: 'solar:user-id-outline', text: 'My Organizer Roles', url: 'my-organizer', iconifyIconString: true },
        { id: 33, avatar: 'solar:users-group-rounded-outline', text: 'Organizers', url: 'organizers', iconifyIconString: true },
        { id: 39, avatar: 'solar:music-note-outline', text: 'DJ Requests', url: 'dj-requests', iconifyIconString: true },
        { id: 73, avatar: 'solar:star-outline', text: 'Promoter Requests', url: 'promoter-requests', iconifyIconString: true },
        // Core
        { id: 2, avatar: usersIcon, text: 'Users', url: 'users' },
        { id: 91, avatar: 'solar:chart-2-outline', text: 'User Activity', url: 'user-activity', iconifyIconString: true },
        { id: 71, avatar: 'solar:user-check-outline', text: 'Join Requests', url: 'join-requests', iconifyIconString: true },
        { id: 72, avatar: 'ion:diamond-outline', text: 'Elite', url: 'elite', iconifyIconString: true },
        { id: 99, avatar: 'solar:case-round-outline', text: 'ZONA for brands', url: 'brands', iconifyIconString: true },
        { id: 94, avatar: 'solar:rocket-2-outline', text: 'Boost', url: 'boost', iconifyIconString: true },
        { id: 40, avatar: 'solar:verified-check-outline', text: 'Verifications', url: 'verifications', iconifyIconString: true },
        { id: 64, avatar: 'solar:gallery-check-outline', text: 'Photo Review', url: 'photo-review', iconifyIconString: true },
        { id: 41, avatar: 'solar:shield-warning-outline', text: 'Moderation', url: 'table-moderation', iconifyIconString: true },
        { id: 34, avatar: 'solar:user-cross-outline', text: 'Deleted Users', url: 'deleted-users', iconifyIconString: true },
        { id: 45, avatar: 'solar:incognito-outline', text: 'Anonymous Users', url: 'anonymous-users', iconifyIconString: true },
        { id: 63, avatar: 'solar:globus-outline', text: 'Web Guests', url: 'web-guests', iconifyIconString: true },
        { id: 98, avatar: 'solar:card-recive-outline', text: 'Payments to Finish', url: 'payments-to-finish', iconifyIconString: true },
        { id: 38, avatar: 'solar:user-plus-outline', text: 'Invites', url: 'discover-invites', iconifyIconString: true },
        { id: 49, avatar: 'solar:eye-outline', text: 'Profile Views', url: 'profile-views', iconifyIconString: true },
        { id: 3, avatar: venuesIcon, text: 'Miami Venues', url: 'business' },
        { id: 41, avatar: venuesIcon, text: 'NYC Venues', url: 'nyc-venues' },
        { id: 30, avatar: 'solar:refresh-outline', text: 'Venue Sync', url: 'venue-sync', iconifyIconString: true },
        { id: 4, avatar: eventsIcon, text: 'Events', url: 'events' },
        { id: 17, avatar: 'solar:chart-outline', text: 'Analytics', url: 'analytics', iconifyIconString: true },
        { id: 48, avatar: 'solar:filter-outline', text: 'Onboarding Funnel', url: 'onboarding-funnel', iconifyIconString: true },
        { id: 32, avatar: 'solar:heart-outline', text: 'Venue Activity', url: 'venue-activity', iconifyIconString: true },
        { id: 20, avatar: 'solar:bell-outline', text: 'Push Notifications', url: 'push-notifications', iconifyIconString: true },
        { id: 37, avatar: 'solar:bell-bing-outline', text: 'Manual Pushes', url: 'manual-pushes', iconifyIconString: true },
        // Plan IDs for manual pushes, like the events list's Event ID column.
        { id: 97, avatar: 'solar:users-group-rounded-outline', text: 'Plans', url: 'plans', iconifyIconString: true },
        { id: 95, avatar: 'solar:calendar-outline', text: 'Push Schedule', url: 'push-schedule', iconifyIconString: true },
        // Venue broadcasts wait here for her approval before they send.
        { id: 101, avatar: 'solar:bell-outline', text: 'Venue Broadcasts', url: 'venue-broadcasts', iconifyIconString: true },
        { id: 52, avatar: 'solar:wineglass-outline', text: 'Tables Tonight', url: 'tables-tonight', iconifyIconString: true },
        { id: 33, avatar: 'solar:pulse-outline', text: 'Engine Stats', url: 'engine-stats', iconifyIconString: true },
        { id: 50, avatar: 'solar:phone-calling-rounded-outline', text: 'Voice Calls', url: 'voice-calls', iconifyIconString: true },
        { id: 53, avatar: 'solar:chat-square-2-outline', text: 'Inbound Texts', url: 'inbound-texts', iconifyIconString: true },
        { id: 96, avatar: 'solar:chat-round-dots-outline', text: '866 Conversations', url: '866-conversations', iconifyIconString: true },
        { id: 35, avatar: 'solar:inbox-line-outline', text: 'User Inboxes', url: 'user-inboxes', iconifyIconString: true },
        { id: 42, avatar: 'solar:smartphone-2-outline', text: 'Shared Devices', url: 'shared-devices', iconifyIconString: true },
        { id: 43, avatar: 'solar:user-cross-rounded-outline', text: 'Flagged Names', url: 'flagged-names', iconifyIconString: true },
        { id: 36, avatar: 'solar:star-outline', text: 'User Event Feedback', url: 'event-feedback', iconifyIconString: true },
        { id: 44, avatar: 'solar:star-shine-outline', text: 'Review Prompts', url: 'review-prompts', iconifyIconString: true },
        // Chats + Promoters hidden — routes still exist, just removed
        // from the sidebar per Katherine's request.
        // { id: 12, avatar: chatIcon, text: 'Chats', url: 'chats' },
        // { id: 10, avatar: promoIcon, text: 'Promoters', url: 'promoters/list' },
        { id: 22, avatar: 'solar:eye-outline', text: 'Demo Leads', url: 'promoter-demos', iconifyIconString: true },
        { id: 14, avatar: 'solar:letter-outline', text: 'Offers', url: 'offers', iconifyIconString: true },
        // Send a drink offers and sends (her call 10/8: its own tab).
        { id: 92, avatar: 'solar:wineglass-triangle-outline', text: 'Drinks', url: 'drinks', iconifyIconString: true },
        // Plan designer backgrounds and title fonts the app loads at start.
        { id: 93, avatar: 'solar:palette-outline', text: 'Plan Themes', url: 'plan-themes', iconifyIconString: true },
        // Financial
        { id: 13, avatar: withdrawalRequestIcon, text: 'Withdrawals', url: 'withdrawal-requests' },
        { id: 16, avatar: 'solar:shield-check-outline', text: 'Disputes', url: 'refund-requests', iconifyIconString: true },
        { id: 25, avatar: 'solar:calendar-mark-outline', text: 'Cancellation Requests', url: 'cancellation-requests', iconifyIconString: true },
        // Other
        { id: 5, avatar: queriesIcon, text: 'Queries', url: 'queries' },
        { id: 8, avatar: advertIcon, text: 'Ads', url: 'ad-management' },
        { id: 19, avatar: 'solar:smartphone-update-outline', text: 'App Versions', url: 'app-versions', iconifyIconString: true },
        { id: 24, avatar: 'solar:document-text-outline', text: 'Legal', url: 'legal', iconifyIconString: true },
        { id: 11, avatar: settingsIcon, text: 'Settings', url: 'settings' },
    ]

    return (
        <>
            <article ref={sidebarRef} className='sidebar'>
                {
                    sideBarContent.map(item => {
                        const badge = badgeMap[item.url] || 0;
                        return (
                            <NavLink to={`/${item.url}`} key={item.id} className='sidebar__item'>
                                <div className='sidebar__item-left'>
                                    {
                                        item.iconifyIconString ? (
                                            <span
                                                className="iconify sidebar__item-left-icon"
                                                style={{ fontSize: 24, color: '#FFFFFF' }}
                                                data-icon={item.avatar}
                                            ></span>
                                        ) : (
                                            <img
                                                className='sidebar__item-left-icon'
                                                src={item.avatar}
                                                alt={`${item.text} icon`}
                                            />
                                        )
                                    }
                                    <p className='sidebar__item-left-writing'>
                                        {item.text}
                                    </p>
                                </div>

                                <div className='sidebar__item-right'>
                                    {badge > 0 && (
                                        <span className='sidebar__badge'>{badge}</span>
                                    )}
                                    <img className='sidebar__item-right-icon' src={chevronRight} alt='right chevron' />
                                </div>
                            </NavLink>
                        );
                    })
                }
            </article>

            <div
                onClick={() => {
                    document.body.classList.remove('show-sidebar')
                }}
                className="sidebar-overlay"
            ></div>

            <BottomTabs />
        </>
    )
}

export default SideBar;
